import { mainLogger as log } from "@/app/logger"
import { loadAll } from "@/app/providers/app_data.loaders"
import { SessionStorage } from "@/app/storage"
import {
  CategoriesList,
  Filter,
  FirmsList,
  FundsList,
  IndustryValues,
  Lookup,
  MetaTerms,
} from "@/app/types"
import { isNotNull } from "@/app/utils"
import { getToken } from "@/lib/get_token"

export class AppData {
  static #instance: AppData | null = null

  static get(): AppData {
    if (AppData.#instance) return AppData.#instance
    return new AppData() // this will set the #instance
  }

  // these are global read-only data initialized when the app is loaded
  // Note - the API loader code caches these responses in LocalStorage
  #id
  categoriesList: CategoriesList
  firmsList: FirmsList
  fundsList: FundsList
  industryMetrics: IndustryValues // includes all intervals
  metaTerms: MetaTerms

  // this is a derived reverse lookup table that is populated
  // after all the above API responses are loaded successfully
  lookup: Lookup

  constructor() {
    this.#id = Math.random()

    if (AppData.#instance) {
      log.warn("Use AppData.get() instead of invoking the constructor!")
      return AppData.#instance
    }

    AppData.#instance = this //set the singleton instance for future use
    this.#startRefresh() // trigger the refresh of data, will complete async
  }

  id() {
    return this.#id
  }

  // return % complete, 0 .. 100
  progress(): number {
    const weights = [
      { o: this.categoriesList, w: 1 },
      { o: this.firmsList, w: 1 },
      { o: this.fundsList, w: 1 },
      { o: this.industryMetrics, w: 4 },
      { o: this.metaTerms, w: 1 },
    ]

    // sum for all items
    const totalCount = weights.reduce((sum, item) => sum + item.w, 0)

    // sum for notNull items
    const completeCount = weights
      .filter((item) => isNotNull(item.o))
      .reduce((sum, item) => sum + item.w, 0)

    return Math.floor((completeCount / totalCount) * 100)
  }

  isLoaded(): boolean {
    return this.progress() == 100
  }

  isLoading(): boolean {
    return !this.isLoaded()
  }

  #lookupRefresh() {
    log.info("Refreshing lookup using app data")
    this.lookup = new Lookup(this)
    //TODO - verify the Lookup counts match the data counts
  }

  #startRefresh() {
    getToken().then(() => {
      let filterKey = "flt_Xr7P"
      const filter = SessionStorage.get("filter") as Filter.Type | null

      if (filter) {
        const appData = new AppData()
        AppData.#instance = appData

        filterKey = filter.key
      }

      loadAll(this, filterKey).then(() => {
        if (this.isLoaded()) this.#lookupRefresh()
        //TODO - use Object.freeze() to freeze this object
      })
    })
  }

  reloadAppData() {
    this.#startRefresh()
  }
}
